<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h4 class="text-lg font-semibold font-heading text-gray-900 dark:text-gray-100">AIO Optimization</h4>
      <span v-if="aio" class="px-3 py-1 rounded-full text-sm font-medium" :class="statusClass">
        {{ statusLabel }}
      </span>
    </div>

    <!-- Error -->
    <div v-if="error" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
      <p class="text-sm text-red-700 dark:text-red-400">{{ error }}</p>
    </div>

    <!-- Progress -->
    <div v-if="progressMessage" class="bg-primary-50 dark:bg-primary-900/20 p-4 rounded-xl">
      <div class="flex items-center gap-3">
        <svg class="animate-spin h-5 w-5 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        <span class="text-sm text-primary-700 dark:text-primary-300">{{ progressMessage }}</span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <svg class="animate-spin h-8 w-8 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>

    <!-- No AIO yet — start analysis -->
    <div v-else-if="!aio" class="text-center py-8">
      <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
        <svg class="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      </div>
      <h4 class="text-base font-semibold font-heading text-gray-900 dark:text-gray-100 mb-2">AI Overview Optimization</h4>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
        Analyze the AI Overview landscape for your keyword and optimize content for AIO citation.
      </p>
      <button @click="handleRunAnalysis" :disabled="starting"
        class="inline-flex items-center px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        {{ starting ? 'Starting...' : 'Run AIO Analysis' }}
      </button>
    </div>

    <!-- Results -->
    <template v-else-if="aio && showResults">
      <!-- Sub-tab navigation -->
      <nav class="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        <button v-for="tab in visibleTabs" :key="tab.key"
          @click="activeTab = tab.key"
          class="px-3 py-2 text-sm font-medium border-b-2 transition-colors"
          :class="activeTab === tab.key
            ? 'border-primary-500 text-primary-600 dark:text-primary-400'
            : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'">
          {{ tab.label }}
        </button>
      </nav>

      <!-- Detection Tab -->
      <div v-if="activeTab === 'detection'">
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
              :class="aio.aio_detection?.present ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'">
              {{ aio.aio_detection?.present ? 'AI Overview Detected' : 'No AI Overview' }}
            </span>
            <span v-if="aio.aio_detection?.format" class="text-sm text-gray-500">Format: {{ aio.aio_detection.format }}</span>
          </div>
          <template v-if="aio.aio_detection?.present">
            <div class="grid grid-cols-3 gap-4 text-sm">
              <div><span class="text-gray-500">Word Count:</span> {{ aio.aio_detection.word_count }}</div>
              <div><span class="text-gray-500">Sources:</span> {{ aio.aio_detection.source_count }}</div>
              <div><span class="text-gray-500">Keyword:</span> {{ aio.aio_detection.keyword }}</div>
            </div>
            <div v-if="aio.aio_detection.text" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
              {{ aio.aio_detection.text }}
            </div>
          </template>
          <p v-else class="text-sm text-gray-500">{{ aio.aio_detection?.reason || 'No AI Overview detected for this keyword.' }}</p>
        </div>
      </div>

      <!-- Sources Tab -->
      <div v-if="activeTab === 'sources' && aio.aio_sources?.length">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <th class="pb-2 font-medium">#</th>
              <th class="pb-2 font-medium">Domain</th>
              <th class="pb-2 font-medium">Organic Rank</th>
              <th class="pb-2 font-medium">Competitor</th>
              <th class="pb-2 font-medium">Content</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="src in aio.aio_sources" :key="src.url" class="border-b dark:border-gray-700">
              <td class="py-2">{{ src.source_position }}</td>
              <td class="py-2">{{ src.domain }}</td>
              <td class="py-2">{{ src.organic_rank || '-' }}</td>
              <td class="py-2">
                <span :class="src.is_competitor ? 'text-green-600 dark:text-green-400' : 'text-gray-400'">
                  {{ src.is_competitor ? 'Yes' : 'No' }}
                </span>
              </td>
              <td class="py-2">
                <span :class="src.has_extracted_content ? 'text-green-600 dark:text-green-400' : 'text-gray-400'">
                  {{ src.has_extracted_content ? 'Extracted' : 'N/A' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Content Analysis Tab -->
      <div v-if="activeTab === 'content' && aio.aio_content_analysis" class="space-y-4">
        <div v-if="aio.aio_content_analysis.opening_statement" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opening Statement</h5>
          <p class="text-sm text-gray-900 dark:text-gray-100">{{ aio.aio_content_analysis.opening_statement }}</p>
        </div>
        <div v-if="aio.aio_content_analysis.key_claims?.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Claims ({{ aio.aio_content_analysis.key_claims.length }})</h5>
          <ul class="list-disc list-inside space-y-1 text-sm text-gray-900 dark:text-gray-100">
            <li v-for="(claim, i) in aio.aio_content_analysis.key_claims" :key="i">{{ claim }}</li>
          </ul>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div v-if="aio.aio_content_analysis.entities_mentioned?.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Entities</h5>
            <div class="flex flex-wrap gap-2">
              <span v-for="(e, i) in aio.aio_content_analysis.entities_mentioned" :key="i"
                class="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full">{{ e }}</span>
            </div>
          </div>
          <div v-if="aio.aio_content_analysis.numbers_cited?.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Data Points</h5>
            <div class="flex flex-wrap gap-2">
              <span v-for="(n, i) in aio.aio_content_analysis.numbers_cited" :key="i"
                class="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded-full">{{ n }}</span>
            </div>
          </div>
        </div>
        <div class="grid grid-cols-3 gap-4 text-sm">
          <div><span class="text-gray-500">Structure:</span> {{ aio.aio_content_analysis.structure_pattern }}</div>
          <div><span class="text-gray-500">Tone:</span> {{ aio.aio_content_analysis.tone }}</div>
          <div><span class="text-gray-500">Answer Directness:</span> {{ aio.aio_content_analysis.answer_directness }}</div>
        </div>
      </div>

      <!-- Signals Checklist Tab -->
      <div v-if="activeTab === 'signals' && aio.signals_checklist?.length">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <th class="pb-2 font-medium">Signal</th>
              <th class="pb-2 font-medium">Category</th>
              <th class="pb-2 font-medium">Score</th>
              <th class="pb-2 font-medium">Status</th>
              <th class="pb-2 font-medium">Recommendation</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(sig, i) in aio.signals_checklist" :key="i" class="border-b dark:border-gray-700">
              <td class="py-2 font-medium text-gray-900 dark:text-gray-100">{{ sig.signal }}</td>
              <td class="py-2">
                <span class="px-2 py-0.5 text-xs rounded-full"
                  :class="{
                    'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300': sig.category === 'structural',
                    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300': sig.category === 'content_quality',
                    'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300': sig.category === 'authority'
                  }">{{ sig.category }}</span>
              </td>
              <td class="py-2">
                <div class="flex items-center gap-2">
                  <div class="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div class="h-full rounded-full" :class="sig.score >= 70 ? 'bg-green-500' : sig.score >= 40 ? 'bg-amber-500' : 'bg-red-500'"
                      :style="{ width: sig.score + '%' }"></div>
                  </div>
                  <span class="text-xs text-gray-500">{{ sig.score }}</span>
                </div>
              </td>
              <td class="py-2">
                <span :class="sig.present_in_client ? 'text-green-600 dark:text-green-400' : 'text-red-500'">
                  {{ sig.present_in_client ? 'Met' : 'Gap' }}
                </span>
              </td>
              <td class="py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate" :title="sig.recommendation">{{ sig.recommendation }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Gap Analysis Tab -->
      <div v-if="activeTab === 'gaps' && aio.gap_analysis?.length">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <th class="pb-2 font-medium">AIO Claim</th>
              <th class="pb-2 font-medium">Coverage</th>
              <th class="pb-2 font-medium">Specificity</th>
              <th class="pb-2 font-medium">Recommendation</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(gap, i) in aio.gap_analysis" :key="i" class="border-b dark:border-gray-700">
              <td class="py-2 text-gray-900 dark:text-gray-100">{{ gap.claim }}</td>
              <td class="py-2">
                <span class="px-2 py-0.5 text-xs rounded-full"
                  :class="{
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': gap.gap_type === 'covered',
                    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300': gap.gap_type === 'partial',
                    'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300': gap.gap_type === 'vague',
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': gap.gap_type === 'missing'
                  }">{{ gap.gap_type }}</span>
              </td>
              <td class="py-2 text-gray-600 dark:text-gray-400">{{ gap.specificity }}</td>
              <td class="py-2 text-gray-600 dark:text-gray-400 max-w-xs truncate" :title="gap.recommendation">{{ gap.recommendation }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Answer Blocks Tab -->
      <div v-if="activeTab === 'answers' && aio.answer_blocks?.length" class="space-y-4">
        <div v-for="(block, i) in aio.answer_blocks" :key="i"
          class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center justify-between mb-2">
            <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ block.question_heading || block.question }}</h5>
            <span class="text-xs text-gray-500">{{ block.word_count }} words</span>
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
            {{ block.answer_block || block.answer }}
          </p>
          <p v-if="block.placement_instructions" class="text-xs text-gray-500 mt-2">
            Placement: {{ block.placement_instructions }}
          </p>
        </div>
      </div>

      <!-- Entities Tab -->
      <div v-if="activeTab === 'entities' && aio.entity_clarifiers?.length" class="space-y-3">
        <div v-for="(ec, i) in aio.entity_clarifiers" :key="i"
          class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center gap-2 mb-2">
            <span class="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-full">
              {{ ec.entity_pair || ec.entity }}
            </span>
            <span v-if="ec.placement_near" class="text-xs text-gray-500">Near: {{ ec.placement_near }}</span>
          </div>
          <p class="text-sm text-gray-700 dark:text-gray-300 italic">"{{ ec.clarifier_sentence }}"</p>
        </div>
      </div>

      <!-- Fact Density Tab -->
      <div v-if="activeTab === 'facts' && aio.fact_enhancements?.length" class="space-y-4">
        <div v-for="(section, i) in aio.fact_enhancements" :key="i"
          class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center justify-between mb-2">
            <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ section.heading }}</h5>
            <span class="text-xs" :class="section.current_fact_count >= 2 ? 'text-green-600' : 'text-amber-600'">
              {{ section.current_fact_count }}/{{ section.target_fact_count }} facts
            </span>
          </div>
          <ul v-if="section.recommendations?.length" class="space-y-2">
            <li v-for="(rec, j) in section.recommendations" :key="j" class="text-sm flex gap-2">
              <span class="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 text-xs rounded-lg flex-shrink-0">{{ rec.fact_type }}</span>
              <span class="text-gray-700 dark:text-gray-300">{{ rec.suggestion }}</span>
            </li>
          </ul>
        </div>
      </div>

      <!-- FAQ Optimization Tab -->
      <div v-if="activeTab === 'faqs' && aio.faq_optimizations?.length" class="space-y-3">
        <div v-for="(faq, i) in aio.faq_optimizations" :key="i"
          class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center gap-2 mb-2">
            <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ faq.question }}</span>
            <span class="px-2 py-0.5 text-xs rounded-full"
              :class="{
                'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300': faq.status === 'optimized',
                'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300': faq.status === 'needs_work',
                'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300': faq.status === 'new' || faq.is_new
              }">{{ faq.is_new ? 'new' : faq.status }}</span>
          </div>
          <ul v-if="faq.issues?.length" class="text-xs text-amber-600 dark:text-amber-400 space-y-1 mb-2">
            <li v-for="(issue, j) in faq.issues" :key="j">{{ issue }}</li>
          </ul>
          <p v-if="faq.optimized_answer" class="text-sm text-gray-700 dark:text-gray-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
            {{ faq.optimized_answer }}
          </p>
        </div>
      </div>

      <!-- Positioning Report Tab -->
      <div v-if="activeTab === 'report' && aio.positioning_report" class="space-y-4">
        <div class="bg-gradient-to-r from-primary-50 to-primary-50 dark:from-primary-900/20 dark:to-primary-900/20 rounded-xl p-6">
          <div class="flex items-center justify-between mb-4">
            <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100">AIO Eligibility Score</h5>
            <span class="text-3xl font-bold text-primary-600 dark:text-primary-400">{{ aio.positioning_report.eligibility_score }}/100</span>
          </div>
          <div class="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span class="text-gray-500">Signals Met:</span>
              {{ aio.positioning_report.signals_met }}/{{ aio.positioning_report.signals_total }}
            </div>
            <div>
              <span class="text-gray-500">Claims Covered:</span>
              {{ aio.positioning_report.claims_covered }}/{{ aio.positioning_report.claims_total }}
            </div>
            <div>
              <span class="text-gray-500">Answer Blocks:</span>
              {{ aio.positioning_report.answer_blocks_generated }}
            </div>
          </div>
        </div>
        <div v-if="aio.positioning_report.expected_impact" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Expected Impact</h5>
          <p class="text-sm text-gray-900 dark:text-gray-100">{{ aio.positioning_report.expected_impact }}</p>
        </div>
        <div v-if="aio.positioning_report.actions_taken?.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Actions Taken</h5>
          <ul class="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li v-for="(action, i) in aio.positioning_report.actions_taken" :key="i">{{ action }}</li>
          </ul>
        </div>
      </div>

      <!-- Monitoring Tab -->
      <div v-if="activeTab === 'monitoring' && aio.monitoring_plan" class="space-y-4">
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Monitoring Frequency: {{ aio.monitoring_plan.monitoring_frequency }}</h5>
          <div v-if="aio.monitoring_plan.tracking_items?.length" class="space-y-2">
            <div v-for="(item, i) in aio.monitoring_plan.tracking_items" :key="i" class="flex items-start gap-2 text-sm">
              <span class="px-1.5 py-0.5 text-xs rounded-lg"
                :class="item.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'">{{ item.priority }}</span>
              <div>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ item.item }}</span>
                <p class="text-gray-500 text-xs">{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="aio.monitoring_plan.trigger_events?.length" class="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <h5 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trigger Events</h5>
          <div v-for="(ev, i) in aio.monitoring_plan.trigger_events" :key="i" class="text-sm mb-2">
            <span class="font-medium text-gray-900 dark:text-gray-100">{{ ev.event }}:</span>
            <span class="text-gray-600 dark:text-gray-400 ml-1">{{ ev.action }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <!-- Run Optimization (after analysis) -->
        <button v-if="aio.status === 'analyzed' && aio.aio_detection?.present"
          @click="handleRunOptimization" :disabled="starting"
          class="inline-flex items-center px-5 py-2 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ starting ? 'Starting...' : 'Run AIO Optimization' }}
        </button>
        <!-- Approve (after optimization) -->
        <button v-if="aio.status === 'awaiting_approval'"
          @click="handleApprove" :disabled="starting"
          class="inline-flex items-center px-5 py-2 rounded-lg text-sm font-medium text-white bg-[#12e40b] hover:bg-[#0fd609] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {{ starting ? 'Approving...' : 'Approve AIO Optimization' }}
        </button>
        <!-- Re-run analysis -->
        <button v-if="aio.status === 'completed' || aio.status === 'failed'"
          @click="handleRunAnalysis" :disabled="starting"
          class="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors">
          Re-run Analysis
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import { runAioAnalysis, getAioOptimization, runAioOptimization, approveAioOptimization } from '~/javascript/api/aio_optimization'

const props = defineProps({
  pageId: { type: String, required: true }
})

const aio = ref(null)
const loading = ref(true)
const starting = ref(false)
const error = ref(null)
const progressMessage = ref('')
const activeTab = ref('detection')

const allTabs = [
  { key: 'detection', label: 'Detection', phase: 'analysis' },
  { key: 'sources', label: 'Sources', phase: 'analysis' },
  { key: 'content', label: 'Content Analysis', phase: 'analysis' },
  { key: 'signals', label: 'Signals', phase: 'analysis' },
  { key: 'gaps', label: 'Gaps', phase: 'optimization' },
  { key: 'answers', label: 'Answer Blocks', phase: 'optimization' },
  { key: 'entities', label: 'Entities', phase: 'optimization' },
  { key: 'facts', label: 'Fact Density', phase: 'optimization' },
  { key: 'faqs', label: 'FAQ Optimization', phase: 'optimization' },
  { key: 'report', label: 'Report', phase: 'reporting' },
  { key: 'monitoring', label: 'Monitoring', phase: 'reporting' }
]

const visibleTabs = computed(() => {
  if (!aio.value) return []
  const s = aio.value.status
  if (['completed', 'reporting'].includes(s)) return allTabs
  if (['awaiting_approval', 'optimized', 'optimizing'].includes(s)) return allTabs.filter(t => t.phase !== 'reporting')
  return allTabs.filter(t => t.phase === 'analysis')
})

const showResults = computed(() => {
  if (!aio.value) return false
  return ['analyzed', 'optimizing', 'awaiting_approval', 'optimized', 'reporting', 'completed', 'failed'].includes(aio.value.status)
})

const statusClass = computed(() => {
  const map = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
    analyzing: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    analyzed: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    optimizing: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    awaiting_approval: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    optimized: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    reporting: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  }
  return map[aio.value?.status] || map.pending
})

const statusLabel = computed(() => {
  const map = {
    pending: 'Pending',
    analyzing: 'Analyzing...',
    analyzed: 'Analysis Complete',
    optimizing: 'Optimizing...',
    awaiting_approval: 'Awaiting Approval',
    optimized: 'Optimized',
    reporting: 'Building Report...',
    completed: 'Complete',
    failed: 'Failed'
  }
  return map[aio.value?.status] || aio.value?.status
})

let subscription = null
let pollTimer = null

onMounted(() => {
  fetchAio()
  subscription = consumer.subscriptions.create(
    { channel: 'AioOptimizationChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (['completed', 'failed', 'awaiting_approval', 'no_aio'].includes(data.step)) {
          stopPolling()
          fetchAio()
          if (data.step !== 'failed') {
            setTimeout(() => { progressMessage.value = '' }, 2000)
          }
        }
      }
    }
  )
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getAioOptimization(props.pageId)
      const a = data.aio_optimization
      if (a && !['pending', 'analyzing', 'optimizing', 'reporting'].includes(a.status)) {
        aio.value = a
        progressMessage.value = ''
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function fetchAio() {
  try {
    const { data } = await getAioOptimization(props.pageId)
    aio.value = data.aio_optimization
  } catch (e) {
    error.value = e.response?.data?.error || e.message
  } finally {
    loading.value = false
  }
}

async function handleRunAnalysis() {
  starting.value = true
  error.value = null
  try {
    const { data } = await runAioAnalysis(props.pageId)
    aio.value = data
    progressMessage.value = 'Starting AIO analysis...'
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.error || e.message
  } finally {
    starting.value = false
  }
}

async function handleRunOptimization() {
  starting.value = true
  error.value = null
  try {
    const { data } = await runAioOptimization(props.pageId)
    aio.value = data
    progressMessage.value = 'Starting AIO optimization...'
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.error || e.message
  } finally {
    starting.value = false
  }
}

async function handleApprove() {
  starting.value = true
  error.value = null
  try {
    const { data } = await approveAioOptimization(props.pageId)
    aio.value = data
    progressMessage.value = 'Building final report...'
    startPolling()
  } catch (e) {
    error.value = e.response?.data?.error || e.message
  } finally {
    starting.value = false
  }
}
</script>
